import type {
  CreateJournalEntryInput,
  UpdateProductInput,
} from "../../../api/WarehouseApi";
import { isBoxId } from "../../../domain/entity-ids";
import { normalizeDate } from "../../../domain/product-form";
import { Date as WarehouseDate } from "../../../stores/Date";
import type { Product } from "../../../stores/Product";
import type { IDate } from "../../../types/date";
import { Measures } from "../../../types/entities";
import type { Action } from "../../../types/entities";
import { normalizeTypeTags } from "../../../utils/typeTags";

export interface ProductUpdateModel {
  name: string;
  photo: string;
  photos?: string[];
  description: string;
  typeTags?: string[];
  measure: Measures;
  quantity: number;
  parentId: string | null;
  manufacturingDate: IDate | null;
  expiryDate: IDate | null;
}

export function resolveDestroySourceBoxId(input: {
  currentParentId: string | null | undefined;
  lastKnownBoxId: string | null;
}): string | null {
  const fromParent = input.currentParentId?.trim() || "";
  if (isBoxId(fromParent)) {
    return fromParent;
  }

  const fromLastKnown = input.lastKnownBoxId?.trim() || "";
  if (isBoxId(fromLastKnown)) {
    return fromLastKnown;
  }

  return null;
}

export function buildTakePutUpdatePayload(
  product: Product,
  nextParentId: string | null,
): UpdateProductInput {
  return {
    name: product.name || "Без названия",
    photo: product.photo || "",
    photos: product.photos,
    description: product.description || "",
    manufacturingDate: normalizeDate(product.manufacturingDate?.toJSON()),
    expiryDate: normalizeDate(product.expiryDate?.toJSON()),
    typeTags: normalizeTypeTags(product.typeTags),
    measure: product.measure || Measures.quantity,
    quantity: product.quantity || 1,
    parentId: nextParentId,
  };
}

export function buildTakePutJournalPayload(
  productId: string,
  action: Action,
  boxId: string,
): CreateJournalEntryInput {
  return {
    action,
    productId,
    fromTo: boxId,
    value: 1,
  };
}

export function applyProductUpdate(
  product: Product,
  updated: ProductUpdateModel,
): void {
  product.name = updated.name;
  product.photo = updated.photo;
  const photos = Array.isArray(updated.photos)
    ? updated.photos.filter((item) => typeof item === "string" && !!item)
    : [];
  if (updated.photo && !photos.includes(updated.photo)) {
    photos.unshift(updated.photo);
  }
  product.photos = photos;
  product.description = updated.description;
  product.typeTags = normalizeTypeTags(updated.typeTags);
  product.measure = updated.measure;
  product.quantity = updated.quantity;
  product.parentId = updated.parentId;
  product.manufacturingDate = updated.manufacturingDate
    ? new WarehouseDate(updated.manufacturingDate)
    : null;
  product.expiryDate = updated.expiryDate
    ? new WarehouseDate(updated.expiryDate)
    : null;
}
